import { Loader2, Inbox } from "lucide-react";

export const PageHeader = ({ kicker, title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div>
      <p className="adm-kicker mb-1.5" data-testid="page-kicker">{kicker}</p>
      <h1 className="text-2xl font-bold tracking-tight text-[#0B1B33]" data-testid="page-title">{title}</h1>
      {sub && <p className="mt-1.5 text-[13px] text-[#5B6B87] max-w-2xl">{sub}</p>}
    </div>
    {children && <div className="flex gap-2">{children}</div>}
  </div>
);

const PILL_STYLES = {
  VERIFIED: "text-emerald-700 border-emerald-300 bg-emerald-50",
  LIVE: "text-emerald-700 border-emerald-300 bg-emerald-50",
  ACTIVE: "text-emerald-700 border-emerald-300 bg-emerald-50",
  MEMBER: "text-violet-700 border-violet-300 bg-violet-50",
  QUALIFIED: "text-emerald-700 border-emerald-300 bg-emerald-50",
  INVITED: "text-blue-700 border-blue-300 bg-blue-50",
  SENT: "text-blue-700 border-blue-300 bg-blue-50",
  DELIVERED: "text-emerald-700 border-emerald-300 bg-emerald-50",
  RUNNING: "text-blue-700 border-blue-300 bg-blue-50",
  COMPLETED: "text-emerald-700 border-emerald-300 bg-emerald-50",
  QUEUED: "text-slate-600 border-slate-300 bg-slate-50",
  DISCOVERED: "text-slate-600 border-slate-300 bg-slate-50",
  EMAIL_FAILED: "text-red-700 border-red-300 bg-red-50",
  FAILED: "text-red-700 border-red-300 bg-red-50",
  BOUNCED: "text-red-700 border-red-300 bg-red-50",
  COMPLAINED: "text-red-700 border-red-300 bg-red-50",
  SUPPRESSED: "text-amber-700 border-amber-300 bg-amber-50",
  NEEDS_CONTACT: "text-amber-700 border-amber-300 bg-amber-50",
  PENDING: "text-amber-700 border-amber-300 bg-amber-50",
  PENDING_APPROVAL: "text-amber-700 border-amber-300 bg-amber-50",
  PENDING_ADMIN_APPROVAL: "text-amber-700 border-amber-300 bg-amber-50",
  VERIFICATION_FAILED: "text-red-700 border-red-300 bg-red-50",
  REJECTED: "text-red-700 border-red-300 bg-red-50",
  OPENED: "text-violet-700 border-violet-300 bg-violet-50",
  CLICKED: "text-violet-700 border-violet-300 bg-violet-50",
};

export const Pill = ({ value }) => (
  <span className={`adm-pill ${PILL_STYLES[value] || "text-slate-600 border-slate-300 bg-slate-50"}`} data-testid={`pill-${String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    {String(value || "—").replace(/_/g, " ")}
  </span>
);

const TRUST_STYLES = {
  VERIFIED: "text-emerald-700 border-emerald-300 bg-emerald-50",
  "AI-GENERATED": "text-violet-700 border-violet-300 bg-violet-50",
  "USER PROVIDED": "text-blue-700 border-blue-300 bg-blue-50",
  DEMO: "text-amber-700 border-amber-300 bg-amber-50",
  DERIVED: "text-slate-600 border-slate-300 bg-slate-50",
};

export const TrustBadge = ({ type }) => (
  <span className={`adm-pill ${TRUST_STYLES[type] || TRUST_STYLES.DERIVED}`} data-testid={`trust-${String(type).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{type}</span>
);

export const trustFor = (p) => {
  const name = p.company_name || "";
  if (/^TEST_/i.test(name)) return "DEMO";
  if (p.website_verified) return "VERIFIED";
  if (p.source === "manual") return "USER PROVIDED";
  return "DERIVED";
};

export const Stat = ({ label, value, testid, hint, onClick, active }) => (
  <div className={`adm-card p-5 ${onClick ? "cursor-pointer hover:border-[#2563EB]/50 transition-colors" : ""} ${active ? "border-[#2563EB] ring-1 ring-[#2563EB]/30" : ""}`}
    data-testid={testid} onClick={onClick} role={onClick ? "button" : undefined}>
    <p className="text-2xl font-bold font-mono text-[#0B1B33]">{typeof value === "number" ? value.toLocaleString() : value ?? "—"}</p>
    <p className="mt-1 adm-kicker !text-[#5B6B87]">{label}</p>
    {hint && <p className="mt-1 text-[10.5px] text-[#93A3BC]">{hint}</p>}
    {onClick && <p className="mt-1.5 text-[9.5px] font-mono uppercase tracking-widest text-[#2563EB]">{active ? "Hide details ↑" : "Click for details ↓"}</p>}
  </div>
);

export const Empty = ({ text, children }) => (
  <div className="adm-card p-10 text-center" data-testid="empty-state">
    <Inbox size={22} className="mx-auto text-[#B6C2D6] mb-3" />
    <p className="text-[13px] text-[#5B6B87]">{text}</p>
    {children && <div className="mt-4">{children}</div>}
  </div>
);

export const Spinner = ({ text = "Loading…" }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-[12px] font-mono uppercase tracking-widest text-[#7C8AA3]" data-testid="loading">
    <Loader2 size={15} className="animate-spin" /> {text}
  </div>
);

export function Table({ columns, rows, testid, empty }) {
  if (!rows) return <Spinner />;
  if (!rows.length) return <Empty text={empty || "No records yet."} />;
  return (
    <div className="adm-card overflow-x-auto" data-testid={testid}>
      <table className="w-full min-w-[640px]">
        <thead><tr>{columns.map((c) => <th key={c.label} className="adm-th">{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.token || r.id || r.email_id || "row"}-${i}`} className="hover:bg-[#F8FAFE] transition-colors" data-testid={`${testid}-row-${i}`}>
              {columns.map((c) => <td key={c.label} className="adm-td">{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
